import React, { useState, useEffect } from 'react';
import { useAuth, useRequest } from 'shared/hooks';
import { checkPermissions } from 'shared/api/auth';

// Higher order component for injecting available routes into a component.
function withRoutes(WrappedComponent, availableRoutes) {
  const routeCheckRequest = availableRoutes
    .map(({ routeToTest }) => (
      routeToTest ? { path: routeToTest.path, method: routeToTest.method || 'GET' } : { skip: true }
    ));

  // False until the permission check succeeds; the route list is empty until then, so a
  // catch-all must wait for it.
  const initialState = {
    enabledRoutes: [],
    enabledLinks: [],
    routesLoaded: false,
  };

  function RouteProvider(props) {
    const [state, setState] = useState(initialState);
    const { request } = useRequest();
    const { user: { id: userId, role } } = useAuth();

    // Finds the routes that the logged in user is allowed to use.
    // Reruns whenever the user id or role changes.
    useEffect(() => {
      (async () => {
        if (!userId || !role) {
          setState(initialState);
          return;
        }

        const result = await request(checkPermissions(routeCheckRequest));
        if (result === undefined) return;
        if (!result) {
          setState(initialState);
          return;
        }

        const enabledRoutes = [];
        const enabledLinks = [];

        // Result is a list of booleans in the same order as the route check request.
        availableRoutes.forEach((r, i) => {
          if (result[i] === true) {
            if (r.RouteComponent) {
              enabledRoutes.push(r);
            }
            if (r.text && r.path) {
              enabledLinks.push(r);
            }
          }
        });
        setState({
          enabledRoutes,
          enabledLinks,
          routesLoaded: true,
        });
      })();
    }, [request, userId, role]);

    return (
      <WrappedComponent
        enabledRoutes={state.enabledRoutes}
        links={state.enabledLinks}
        routesLoaded={state.routesLoaded}
        {...props}
      />
    );
  }

  return RouteProvider;
}

export default function connectRoutes(routes) {
  return (component) => withRoutes(component, routes);
}
